// ─────────────────────────────────────────────────────────
// services/encryptionFields.ts — Sensitive field registry
//
// Maps each collection name → the set of field keys that must
// be encrypted before leaving the device.
//
// Encryption philosophy (Phase 5):
//   OLD: entire document → { _enc: string } blob
//   NEW: only sensitive fields encrypted inline as { _enc: string }
//
// What "sensitive" means here:
//   - Financial amounts (salary, bank figures, invoice totals)
//   - Personal identifiers (PAN, Aadhaar, UAN, mobile, bank a/c)
//   - Contact details that are PII (phone, email, address)
//   - Tax identifiers (GSTIN, TDS amounts)
//   - Bank details (account number, IFSC, bank name)
//
// What stays PLAINTEXT (must be queryable / filterable):
//   - id, companyId, createdAt, updatedAt
//   - status, date, projId, vendorId, empId (foreign keys)
//   - name/label fields used in display/search
//   - category, type, source fields used in filters
//
// Collections NOT in this map → zero field encryption.
// ─────────────────────────────────────────────────────────

export type SensitiveFields = ReadonlySet<string>

const s = (...fields: string[]): SensitiveFields => new Set(fields)

export const SENSITIVE_FIELDS: Readonly<Record<string, SensitiveFields>> = {

  // ── HR & Payroll ──────────────────────────────────────
  emp: s(
    'mobile', 'bank', 'ifsc', 'bankName', 'branch',
    'pan', 'aadhaar', 'uan', 'pf',
    'monthlySalary', 'dailyRate', 'address',
  ),

  sal: s(
    'gross', 'advance', 'pfEmp', 'pfEr', 'esicEmp', 'esicEr',
    'net', 'netWords',
  ),

  adv: s(
    'amount',
  ),

  // ── Finance ───────────────────────────────────────────
  exp: s(
    'amount', 'gstNum', 'billNo',
  ),

  cf: s(
    'amount',
  ),

  petty: s(
    'amount',
  ),

  recon: s(
    'debit', 'credit', 'balance', 'desc',
  ),

  // ── Procurement ───────────────────────────────────────
  vendor: s(
    'phone', 'email', 'address', 'gstin', 'pan', 'bank',
    'ifsc', 'bankName', 'contact',
  ),

  invoice: s(
    'basic', 'gst', 'totalAmount', 'tds', 'retention', 'netPayable',
  ),

  po: s(
    'grandTotal', 'advancePaid', 'receivedValue',
  ),

  quotations: s(
    'totalAmount', 'phone', 'email',
  ),

  // ── Tenders & Billing ─────────────────────────────────
  recv: s(
    'grossAmount', 'retention', 'tdsDeducted', 'netReceivable', 'receivedAmount',
  ),

  tender: s(
    'emdAmount', 'bidValue', 'contractValue',
  ),

  emd: s(
    'amount',
  ),

  ret: s(
    'amount',
  ),

  einvoices: s(
    'totalTaxableValue', 'totalTax', 'totalInvoiceValue',
  ),

  // ── Projects ──────────────────────────────────────────
  proj: s(
    'value', 'contact',
  ),

  // ── Compliance ───────────────────────────────────────
  tds: s(
    'amount', 'pan', 'tan',
  ),

  ins: s(
    'premium', 'sumInsured', 'policyNo',
  ),

} as const

/**
 * Returns true if this collection has ANY sensitive fields defined.
 */
export function hasSensitiveFields(collection: string): boolean {
  return collection in SENSITIVE_FIELDS
}

/**
 * Returns the set of sensitive field keys for a collection,
 * or an empty set if none are defined.
 */
export function getSensitiveFields(collection: string): SensitiveFields {
  return SENSITIVE_FIELDS[collection] ?? new Set()
}
